import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';

import { useCollection } from './CollectionProvider';
import { useSheetsCommon } from './CommonProvider';
import { filtersReducer, ACTIONS } from '../helpers/reducers/filters';
import Services, { cancelRequests } from '../helpers/sdkServices';

const SheetFilterContext = React.createContext({});
const useSheetFilter = () => useContext(SheetFilterContext);

const initialState = {
  versionSets: [],
  allVersionSets: [],
  tags: [],
  versionSetsLoading: false,
  versionSetsError: false,
  allVersionSetsLoading: false,
  allVersionSetsError: false,
  tagsLoading: false,
  tagsError: false,
  offset: 0,
};

const SheetFilterProvider = ({ children }) => {
  const [state, dispatch] = useReducer(filtersReducer, initialState);
  const projectId = useCurrentProjectId();

  const { sheetContainerType, isPluginEnabled } = useSheetsCommon();
  const { isFeatureEnabled: enableCollectionFeature } = useCollection();

  const loadTags = useCallback(async () => {
    try {
      dispatch({ type: ACTIONS.LOAD_TAG_START });
      const { results } = await Services.Sheets.getTags(projectId, {
        sheetContainerType,
      });
      dispatch({
        type: ACTIONS.LOAD_TAG_COMPLETE,
        payload: { tags: results },
      });
      return results;
    } catch (err) {
      dispatch({ type: ACTIONS.LOAD_TAG_ERROR });
      return null;
    }
  }, [projectId, sheetContainerType]);

  const loadAllVersionSets = useCallback(async () => {
    try {
      dispatch({ type: ACTIONS.LOAD_ALL_SET_START });
      const res = await Services.VersionSets.getAllVersionSets(projectId, {
        limit: 1000,
        sheetContainerType,
        collectionId: '*',
      });
      dispatch({
        type: ACTIONS.LOAD_ALL_SET_COMPLETE,
        payload: {
          allVersionSets: res,
        },
      });
      return res;
    } catch (err) {
      dispatch({ type: ACTIONS.LOAD_ALL_SET_ERROR });
      return null;
    }
  }, [projectId, sheetContainerType]);

  const loadVersionSets = useCallback(
    async (offset = 0) => {
      try {
        dispatch({ type: ACTIONS.LOAD_SET_START });
        const limit = 200;
        const res = await Services.VersionSets.getVersionSets(projectId, {
          offset,
          limit,
          sheetContainerType,
        });
        if (res) {
          dispatch({
            type: ACTIONS.LOAD_SET_COMPLETE,
            payload: {
              versionSets: res.versionSets,
              offset,
            },
          });
          const newOffset = offset + limit;
          const { totalResults } = res.pagination;
          if (totalResults - newOffset > 0) {
            loadVersionSets(newOffset);
          }
        }
        return res;
      } catch (err) {
        dispatch({ type: ACTIONS.LOAD_SET_ERROR, payload: { offset } });
        return null;
      }
    },
    [projectId, sheetContainerType],
  );

  const retryAllVersionSets = useCallback(() => {
    loadAllVersionSets();
  }, [loadAllVersionSets]);

  const retryVersionSets = useCallback(() => {
    loadVersionSets(state.offset);
  }, [loadVersionSets, state.offset]);

  const cancelLoadTags = () => {
    Services.VersionSets.loadTagsCancelToken &&
      cancelRequests([Services.VersionSets.loadTagsCancelToken]);
  };

  const cancelLoadAllVersionSets = () => {
    Services.VersionSets.loadAllVersionSetsCancelToken &&
      cancelRequests([Services.VersionSets.loadAllVersionSetsCancelToken]);
  };

  const cancelLoadVersionSets = () => {
    Services.VersionSets.loadVersionSetsCancelToken &&
      cancelRequests([Services.VersionSets.loadVersionSetsCancelToken]);
  };

  useEffect(() => {
    if (!isPluginEnabled) return undefined;

    loadTags();

    return () => {
      cancelLoadTags();
    };
  }, [isPluginEnabled, loadTags]);

  useEffect(() => {
    if (!isPluginEnabled) return undefined;

    enableCollectionFeature ? loadAllVersionSets() : loadVersionSets();

    return () =>
      enableCollectionFeature
        ? cancelLoadAllVersionSets()
        : cancelLoadVersionSets();
  }, [
    isPluginEnabled,
    enableCollectionFeature,
    loadAllVersionSets,
    loadVersionSets,
  ]);

  const value = useMemo(
    () => ({
      ...state,
      retryTags: loadTags,
      retryAllVersionSets,
      retryVersionSets,
    }),
    [state, loadTags, retryAllVersionSets, retryVersionSets],
  );

  return (
    <SheetFilterContext.Provider value={value}>
      {children}
    </SheetFilterContext.Provider>
  );
};

SheetFilterProvider.propTypes = {
  children: PropTypes.node,
};

SheetFilterProvider.displayName = 'SheetFilterProvider';

export { useSheetFilter, SheetFilterProvider };
